import type { AssistantPendingDTO } from './assistant';
import type { AuditViaDTO, Id, ISODate } from './common';
import type { TaskStatus } from './tasks';

// AI S9 "Giao việc có theo dõi": the delegation enums, limits and DTOs. Every enum below is also a CHECK in 030;
// `tests/unit/delegation-contracts.test.ts` reads the migration and compares. The API's zod schemas
// (`modules/delegations/schema.ts`) take their bounds from here.

/** §5.2: where a delegation stands. `deadline_requested` and `question` return to the state they left (`stateBefore`). */
export const DELEGATION_STATES = ['waiting', 'accepted', 'deadline_requested', 'question'] as const;
export type DelegationState = (typeof DELEGATION_STATES)[number];
/** Where a delegation stands, as §8.1 says it: web `labels.ts` (its block, its audit rows) and Hana's prompt read it. */
export const DELEGATION_STATE_LABEL: Readonly<Record<DelegationState, string>> = {
  waiting: 'Chờ nhận', accepted: 'Đã nhận', deadline_requested: 'Xin lùi hạn', question: 'Đang hỏi lại',
};

/** §4.2: what `delegation_signals` records, append-only. Every kind but `progress_asked` is also an audit action. */
export const DELEGATION_SIGNAL_KINDS = ['delegated', 'accepted', 'deadline_requested', 'deadline_accepted',
  'deadline_declined', 'deadline_withdrawn', 'question', 'clarified', 'progress_asked', 'tracking_off', 'tracking_on',
  'restored'] as const;
export type DelegationSignalKind = (typeof DELEGATION_SIGNAL_KINDS)[number];
/**
 * Each signal as the product names it (S9 L4 D143 P6a): the activity log's words (web `audit-labels.ts` reads them),
 * plus "Hỏi tiến độ", which is no audit action. Persona reads these, never the code.
 */
export const DELEGATION_SIGNAL_LABEL: Readonly<Record<DelegationSignalKind, string>> = {
  delegated: 'Giao việc', accepted: 'Nhận việc', deadline_requested: 'Xin lùi hạn',
  deadline_accepted: 'Chấp nhận lùi hạn', deadline_declined: 'Từ chối lùi hạn', deadline_withdrawn: 'Rút yêu cầu lùi hạn',
  question: 'Hỏi lại', clarified: 'Đã rõ', progress_asked: 'Hỏi tiến độ', tracking_off: 'Tắt theo dõi',
  tracking_on: 'Bật theo dõi', restored: 'Hoàn tác giao việc',
};

/**
 * §4.4: when the delegator's assistant proposes a reminder, in working days (S3 calendar). `beforeDueDays` 0 is off;
 * `blockedDays` and `silentDays` are off as null.
 */
export type DelegationCadence = {
  beforeDueDays: number; blockedDays: number | null; overdue: boolean; silentDays: number | null;
};
export const DELEGATION_CADENCE_DEFAULT: Readonly<DelegationCadence> =
  { beforeDueDays: 1, blockedDays: 2, overdue: true, silentDays: 3 };
/** The integer range of each numeric cadence key (030 `work_delegations_cadence`). */
export const DELEGATION_CADENCE_LIMITS = {
  beforeDueDays: { min: 0, max: 5 }, blockedDays: { min: 1, max: 10 }, silentDays: { min: 1, max: 15 },
} as const;

/** §6.5: contacts (nudges plus progress asks) one sender may make to one person per business day. */
export const NUDGE_PAIR_LIMIT = 3;
/** §4.4: the expected outcome and a deadline request's note. A "Hỏi lại" comment uses the comment limit. */
export const DELEGATION_OUTCOME_MAX = 500;
export const DELEGATION_NOTE_MAX = 500;

/** §4.3 (P9): the three delegated rules 030 lets the rule tables hold. `PROACTIVE_RULES` gains them in L2. */
export const DELEGATED_S9_RULES = ['delegated.due_soon', 'delegated.overdue', 'delegated.blocked'] as const;
export type DelegatedS9Rule = (typeof DELEGATED_S9_RULES)[number];

/** §10: 409, 409, 400 and 429. */
export const DELEGATION_ERROR_CODES = ['DELEGATION_STATE', 'DELEGATION_STALE', 'DEADLINE_NOT_LATER',
  'NUDGE_PAIR_LIMIT'] as const;
export type DelegationErrorCode = (typeof DELEGATION_ERROR_CODES)[number];

/**
 * One line of the block's recent history. `details` carries only the dates, the reason and the comment id of the
 * signal's payload — never `payload.before`. `via` is the reader's view of who carried it (`toAuditViaDTO`, 'feed').
 */
export type DelegationSignalDTO = {
  id: Id; kind: DelegationSignalKind; round: number; actorId: Id; actorName: string; at: string;
  details: { from?: ISODate | null; to?: ISODate | null; reason?: 'answered' | 'deleted'; commentId?: Id };
  via: AuditViaDTO | null;
};

/**
 * `GET /api/workplace/items/:id/delegation` (§8.1): the card's delegation as one of its two parties sees it. The
 * assignee gets it only while the row is active; the delegator also while tracking is off (P17). `contactsToday` is the
 * delegator's pair count (§6.5), null for the assignee. `cadence` and `tracking` are the delegator's own follow-up
 * settings, null for the assignee (D39). `signals` are the latest three, progress asks included (P6).
 */
export type DelegationBlockDTO = {
  itemId: Id; role: 'delegator' | 'assignee';
  delegator: { id: Id; name: string }; assignee: { id: Id; name: string; active: boolean };
  state: DelegationState; stateBefore: DelegationState | null; requestedDeadline: ISODate | null;
  requestNote: string | null; questionCommentId: Id | null; outcome: string | null; cadence: DelegationCadence | null;
  tracking: boolean | null; round: number; version: number; lastSignalAt: string;
  /** The card is open and not deleted. */
  actionable: boolean;
  contactsToday: { used: number; limit: number } | null;
  signals: DelegationSignalDTO[];
};

/** `POST …/delegation/respond` (the assignee's three buttons, and "Đã rõ" on a question). */
export type DelegationRespondBody =
  | { action: 'accept'; round: number; version: number }
  | { action: 'request_deadline'; round: number; version: number; deadline: ISODate; note?: string }
  | { action: 'question'; round: number; version: number; text: string }
  | { action: 'clarified'; round: number; version: number };

/** `POST …/delegation/decide` (the delegator on a deadline request). */
export type DelegationDecideBody = { decision: 'accept' | 'decline'; round: number; version: number };

/** `PATCH …/delegation` (the delegator): at least one of the three; `version` is required with `tracking` (P16). */
export type DelegationTrackBody = {
  round: number; version?: number; outcome?: string | null; cadence?: DelegationCadence; tracking?: boolean;
};

/**
 * `POST …/delegation/progress` (§8.2): the delegator's "Hỏi tiến độ", built by code from structured fields only — never
 * text the assignee wrote (no comment, note, question or blocker text) and never the assignee's Persona: the heading is
 * the person's given name with "✦ (tự động)". `blocked.children` are the blocked direct children the asker sees (the
 * structured reason); `children` counts only the direct child tasks the asker sees. `signals` are the latest three,
 * progress asks excluded (P6). `stale` is `silentWorkdays ≥ cadence.silentDays` (never when that is off).
 */
export type DelegationProgressDTO = {
  itemId: Id; title: string; heading: string;
  assignee: { id: Id; name: string };
  status: TaskStatus;
  blocked: { since: string | null; children: Array<{ id: Id; title: string }> } | null;
  children: { done: number; total: number };
  deadline: ISODate | null; daysLeft: number | null;
  state: DelegationState; round: number;
  lastSignalAt: string; silentWorkdays: number; silentDays: number | null; stale: boolean;
  signals: DelegationSignalDTO[];
  contactsToday: { used: number; limit: number };
};

/**
 * `POST …/delegation/nudge` (§8.2 last bullet, L3 D82): "Nhắc {tên}" from the progress answer. The delegator's pending
 * `work.nudge` card (forceConfirm, on its Sửa form, the `delegated.silent` note); nothing reaches the assignee until it
 * is confirmed through `/api/assistant/actions/:id/confirm` (D3). Rejecting it sends nothing. D105: it stages only while
 * the progress answer's `stale` holds; otherwise 409 DELEGATION_STATE, which the FE shows as "Việc vừa thay đổi, xem
 * lại trước khi bấm" and refetches the answer.
 */
export type DelegationNudgeDTO = { pending: AssistantPendingDTO };

/** §8.3: the two lists — "Tôi đã giao" (`mine`, the delegator's) and "Nhờ tôi" (`for_me`, the assignee's). */
export const DELEGATION_LIST_SIDES = ['mine', 'for_me'] as const;
export type DelegationListSide = (typeof DELEGATION_LIST_SIDES)[number];
/** §8.3 "Tôi đã giao", in display order (first match wins). */
export const DELEGATOR_GROUPS = ['decide', 'risk', 'blocked', 'waiting', 'on_track'] as const;
export type DelegatorGroup = (typeof DELEGATOR_GROUPS)[number];
/** §8.3 "Nhờ tôi", in display order. */
export const ASSIGNEE_GROUPS = ['waiting', 'due_soon', 'doing'] as const;
export type AssigneeGroup = (typeof ASSIGNEE_GROUPS)[number];
/** Each group's heading as the lists show it (web `format.ts` and Persona read these; S9 L4 D143 P6a). */
export const DELEGATOR_GROUP_LABEL: Readonly<Record<DelegatorGroup, string>> = {
  decide: 'Cần quyết', risk: 'Có rủi ro', blocked: 'Bị chặn', waiting: 'Chờ nhận', on_track: 'Đúng tiến độ',
};
export const ASSIGNEE_GROUP_LABEL: Readonly<Record<AssigneeGroup, string>> = {
  waiting: 'Chờ tôi nhận', due_soon: 'Sắp hạn', doing: 'Đang làm',
};

/**
 * One active delegation in `GET /api/delegations` and `delegation.list` (§8.3, S9 P13): structured fields only — never
 * the request note, a question, a blocker or an outcome text, and never anyone's Persona name. `contactsToday` is the
 * delegator's pair count with the assignee (§6.5), null on the `for_me` side. L3 T1: `assignee.active` is the account's
 * status as a bare flag (no reason); `requestedDeadline` is set only in `deadline_requested`; `blockedSince` only while
 * the card is blocked; `silentWorkdays` is the HCM workdays since `lastSignalAt` — the very number the grouping reads.
 */
export type DelegationListItemDTO = {
  itemId: Id; title: string;
  delegator: { id: Id; name: string }; assignee: { id: Id; name: string; active: boolean };
  state: DelegationState; round: number; version: number;
  deadline: ISODate; overdue: boolean; blocked: boolean; lastSignalAt: string;
  requestedDeadline: ISODate | null; blockedSince: string | null; silentWorkdays: number;
  group: DelegatorGroup | AssigneeGroup;
  contactsToday: { used: number; limit: number } | null;
};
export type DelegationListDTO = { side: DelegationListSide; items: DelegationListItemDTO[] };
